import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Smartphone, Share, PlusSquare, Download, CheckCircle2, Chrome, Apple, MonitorSmartphone, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import usePageSeo from "@/hooks/usePageSeo";

// Detect the visitor's platform once at mount. Kept simple — we only need
// to pick between Android (Chrome-family), iOS, and desktop.
function detectPlatform() {
  if (typeof navigator === "undefined") return "unknown";
  const ua = (navigator.userAgent || "").toLowerCase();
  if (/iphone|ipad|ipod/.test(ua) || (ua.includes("mac") && "ontouchend" in document)) return "ios";
  if (/android/.test(ua)) return "android";
  return "desktop";
}

export default function Install() {
  const [platform] = useState(detectPlatform);
  const [installEvent, setInstallEvent] = useState(null);
  const [installed, setInstalled] = useState(false);
  const isStandalone = typeof window !== "undefined"
    && (window.matchMedia?.("(display-mode: standalone)").matches
        || window.navigator?.standalone === true);

  usePageSeo({
    title: "Download DPP for iPhone & Android — Dividend Portfolio Planner",
    description: "Install the Dividend Portfolio Planner app for iPhone, Android, or desktop PWA. One-tap access, push alerts, offline-friendly.",
    path: "/download",
    ogSlug: "download",
  });

  // Android Chrome fires this event when the site is installable. We
  // capture it so users can tap our own "Install" button instead of
  // hunting for the browser menu.
  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setInstallEvent(null);
      toast.success("DPP installed! Look for it on your home screen.");
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!installEvent) {
      toast.error("Install prompt not available yet — use your browser's Add-to-Home-Screen menu.");
      return;
    }
    installEvent.prompt();
    const choice = await installEvent.userChoice;
    if (choice.outcome === "accepted") {
      toast.success("Installing DPP…");
    } else {
      toast("You can install anytime by tapping this button again.");
    }
    setInstallEvent(null);
  };

  return (
    <main className="max-w-3xl mx-auto px-6 py-14">
      <Link to="/" className="mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-primary inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="w-3 h-3" /> Back to DPP
      </Link>

      <header className="text-center">
        <div className="mx-auto w-20 h-20 rounded-2xl overflow-hidden shadow-lg border border-border">
          <img src="/icons/icon-192.png" alt="DPP icon" className="w-full h-full object-cover" />
        </div>
        <div className="mono text-[11px] uppercase tracking-[0.25em] text-secondary mt-4">Download for phone &amp; desktop</div>
        <h1 className="serif text-4xl sm:text-5xl mt-2">
          Get <span className="italic text-primary">Dividend Portfolio Planner</span> on your device
        </h1>
        <p className="text-muted-foreground mt-4 max-w-lg mx-auto text-sm">
          No app-store required. DPP installs as a lightweight web app that lives on your home screen — same icon, same feel, opens instantly.
        </p>
        {isStandalone && (
          <Badge variant="outline" className="mt-4 mono text-[10px] uppercase" data-testid="install-standalone-badge">
            <CheckCircle2 className="w-3 h-3 mr-1 text-primary" /> Already installed
          </Badge>
        )}
      </header>

      {/* Platform-specific instructions */}
      <div className="mt-10 space-y-4">
        {(platform === "android" || platform === "desktop") && (
          <AndroidInstructions
            installEvent={installEvent}
            installed={installed}
            onInstall={promptInstall}
            emphasized={platform === "android"}
          />
        )}
        {(platform === "ios" || platform === "desktop") && (
          <IOSInstructions emphasized={platform === "ios"} />
        )}
        {platform === "desktop" && <DesktopInstructions />}
      </div>

      {/* Perks strip */}
      <section aria-labelledby="perks-heading" className="mt-10 rounded-2xl border border-primary/20 bg-primary/5 p-6">
        <h2 id="perks-heading" className="mono text-[10px] uppercase tracking-[0.25em] text-secondary text-center">Why install?</h2>
        <ul className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <Perk label="Home-screen icon" sub="Tap once, open in a second — no browser tab hunting" />
          <Perk label="Full-screen view" sub="Runs in its own window with no address bar clutter" />
          <Perk label="Zero storage cost" sub="Under 3 MB · Uninstall in one tap anytime" />
        </ul>
      </section>

      <p className="mt-8 text-center text-[11px] mono text-muted-foreground uppercase tracking-widest">
        Educational tool · Not investment advice
      </p>
    </main>
  );
}

function AndroidInstructions({ installEvent, installed, onInstall, emphasized }) {
  return (
    <Card className={emphasized ? "border-primary/30" : ""} data-testid="install-android">
      <CardContent className="p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
            <Chrome className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="serif text-2xl">Android · Chrome, Edge, Brave</h2>
              {emphasized && <Badge className="mono text-[10px] uppercase">Detected</Badge>}
            </div>
            <p className="text-muted-foreground mt-1 text-sm">Fastest path — one tap and you&apos;re done.</p>

            {(installEvent || installed) && (
              <div className="mt-4">
                <Button
                  onClick={onInstall}
                  disabled={installed || !installEvent}
                  className="rounded-full"
                  data-testid="install-android-cta"
                >
                  {installed ? <><CheckCircle2 className="w-4 h-4 mr-2" /> Installed</> : <><Download className="w-4 h-4 mr-2" /> Install DPP</>}
                </Button>
                <div className="mono text-[10px] text-muted-foreground mt-2">
                  If the button doesn&apos;t appear, use the manual steps below.
                </div>
              </div>
            )}

            <ol className="mt-4 space-y-2 text-sm list-decimal list-inside">
              <li>Tap the <strong>⋮ menu</strong> in the top-right of your browser.</li>
              <li>Choose <strong>&ldquo;Install app&rdquo;</strong> or <strong>&ldquo;Add to Home screen&rdquo;</strong>.</li>
              <li>Confirm the name (DPP) and tap <strong>Install</strong>.</li>
              <li>Look for the DPP icon on your home screen — tap to open in full-screen mode.</li>
            </ol>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function IOSInstructions({ emphasized }) {
  return (
    <Card className={emphasized ? "border-primary/30" : ""} data-testid="install-ios">
      <CardContent className="p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
            <Apple className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="serif text-2xl">iPhone &amp; iPad · Safari</h2>
              {emphasized && <Badge className="mono text-[10px] uppercase">Detected</Badge>}
            </div>
            <p className="text-muted-foreground mt-1 text-sm">
              iOS doesn&apos;t offer a one-tap installer, but it&apos;s only a few taps in Safari&apos;s Share menu.
            </p>

            <ol className="mt-4 space-y-2 text-sm list-decimal list-inside">
              <li>Open <strong>dividendportfolioplanner.com</strong> in <strong>Safari</strong> (not Chrome).</li>
              <li>Tap the <strong>Share button</strong> <Share className="inline w-4 h-4 align-text-bottom mx-0.5" /> at the bottom of the screen.</li>
              <li>Scroll and tap <strong>&ldquo;Add to Home Screen&rdquo;</strong> <PlusSquare className="inline w-4 h-4 align-text-bottom mx-0.5" />.</li>
              <li>Keep the name as <strong>DPP</strong> and tap <strong>Add</strong>.</li>
              <li>Find the DPP icon on your home screen and tap to open — it opens full-screen without Safari&apos;s address bar.</li>
            </ol>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DesktopInstructions() {
  return (
    <Card data-testid="install-desktop">
      <CardContent className="p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-secondary/10 text-secondary grid place-items-center shrink-0">
            <MonitorSmartphone className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h2 className="serif text-2xl">Desktop · Chrome, Edge, Safari 17+</h2>
            <p className="text-muted-foreground mt-1 text-sm">
              You can install DPP on your Mac, Windows, or Chromebook too — it opens in its own window and shows up in your app dock.
            </p>
            <ol className="mt-4 space-y-2 text-sm list-decimal list-inside">
              <li>Look for the <strong>install icon</strong> in the address bar (a monitor with a down-arrow, or the ⋮ menu → <em>Install DPP…</em>).</li>
              <li>Click it and confirm.</li>
              <li>DPP now appears in your Applications / Start menu / dock.</li>
            </ol>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Perk({ label, sub }) {
  return (
    <li className="rounded-lg bg-background border border-border/60 p-3">
      <div className="flex items-center gap-1.5">
        <Smartphone className="w-3.5 h-3.5 text-primary" />
        <div className="mono text-[10px] uppercase tracking-widest">{label}</div>
      </div>
      <div className="text-[13px] text-muted-foreground mt-1 leading-snug">{sub}</div>
    </li>
  );
}
